import type { ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { colors } from '../../tokens/colors';
import { borderRadius } from '../../tokens/spacing';

/**
 * Seção de formulário complexo.
 *
 * O DESIGN.md prescreve, para "Complex Forms", agrupamento em `fieldset` com
 * legenda — e a varredura das 84 telas do frontend encontrou **zero** `<fieldset>`.
 * Sem agrupamento semântico, um formulário fiscal de 40 campos é uma lista plana
 * para quem navega por teclado ou leitor de tela.
 */

export interface FormSectionProps {
  readonly legend: ReactNode;
  readonly description?: ReactNode;
  readonly children: ReactNode;
  /** Duas colunas a partir de telas médias, como nos formulários do protótipo. */
  readonly columns?: 1 | 2;
  readonly className?: string;
}

export function FormSection({
  legend,
  description,
  children,
  columns = 1,
  className,
}: FormSectionProps): JSX.Element {
  return (
    <fieldset
      className={cn('ds-form-section', className)}
      style={{
        margin: 0,
        marginBottom: '1.5rem',
        padding: '1rem',
        border: `1px solid ${colors.border.default}`,
        borderRadius: borderRadius.xl,
        background: colors.surface.card,
        minWidth: 0,
      }}
    >
      <legend
        style={{
          padding: '0 0.5rem',
          fontSize: '0.875rem',
          fontWeight: 600,
          color: colors.primary.DEFAULT,
        }}
      >
        {legend}
      </legend>

      {description ? (
        <p
          style={{
            margin: '0 0 1rem',
            fontSize: '0.75rem',
            lineHeight: '1rem',
            color: colors.text.secondary,
          }}
        >
          {description}
        </p>
      ) : null}

      <div
        style={{
          display: 'grid',
          gap: '1rem',
          gridTemplateColumns: columns === 2 ? 'repeat(auto-fit, minmax(240px, 1fr))' : '1fr',
        }}
      >
        {children}
      </div>
    </fieldset>
  );
}
